// Плашка «Здесь»: что известно о месте под кликом и насколько точно.
//
// Известное — галочкой. Интервальное — знаком «у порога» и мини-шкалой: полоса —
// свойство в окрестности клика, засечки — пороги норм. Порог внутри полосы и
// есть ответ на вопрос «почему фильтр не поставлен»: здесь он лежит, а в двухстах
// метрах — уже нет. Свойства места не превращаются в значения фильтров: это
// факты точки, а не выбор человека.

import { formatNumber, useLang, useT } from '../i18n'
import type { RulesPoint } from '../api'
import { factScale } from './factScale'
import { coverageNote, type RulesCoverage } from './coverage'

function Mark({ sure }: { sure: boolean | null }) {
  if (sure == null) return <span aria-hidden className="w-3 shrink-0 text-faint">?</span>
  return sure
    ? <span aria-hidden className="w-3 shrink-0 text-ok">✓</span>
    : <span aria-hidden className="w-3 shrink-0 text-buoy">!</span>
}

function Scale({ interval, thresholds, unit, fmt }: {
  interval: [number, number]
  thresholds: number[]
  unit: string
  fmt: (v: number) => string
}) {
  const s = factScale(interval, thresholds)
  return (
    <div className="relative mt-1 h-3" aria-hidden>
      <div className="absolute inset-x-0 top-1.5 h-px bg-hull" />
      <div className="absolute top-0.5 h-2 rounded-sm bg-sonar/40"
        style={{ left: `${s.lo}%`, width: `${Math.max(s.hi - s.lo, 1)}%` }} />
      {s.ticks.map(tk => (
        <div key={tk.value} title={`${fmt(tk.value)} ${unit}`}
          className={`absolute top-0 h-3 w-0.5 ${tk.inside ? 'bg-buoy' : 'bg-mist'}`}
          style={{ left: `${tk.pct}%` }} />
      ))}
    </div>
  )
}

function Row({ sure, label, children, scale }: {
  sure: boolean | null
  label: string
  children: React.ReactNode
  scale?: React.ReactNode
}) {
  return (
    <div className="py-1">
      <div className="flex items-baseline gap-1.5">
        <Mark sure={sure} />
        <span className="w-[86px] shrink-0 text-faint">{label}</span>
        <span className="min-w-0 text-foam">{children}</span>
      </div>
      {scale && <div className="ml-[18px]">{scale}</div>}
    </div>
  )
}

/** Интервал словами: «18–26 м». */
function range(iv: [number, number], unit: string, fmt: (v: number) => string): string {
  const [a, b] = iv
  return a === b ? `${fmt(a)} ${unit}` : `${fmt(a)}–${fmt(b)} ${unit}`
}

const inside = (iv: [number, number], ths: number[]) => ths.some(v => v > iv[0] && v < iv[1])

export function PointFacts({ point, coverage, onClear }: {
  point: RulesPoint
  coverage?: RulesCoverage | null
  onClear: () => void
}) {
  const t = useT()
  const { lang } = useLang()
  const fmt = (v: number) => formatNumber(lang, v, 1)
  const d = point.district
  const several = point.districts.length > 1
  const unsure = several || point.places_edge.length > 0
    || (point.depth != null && inside(point.depth, point.thresholds.depth))
    || (point.from_shore_nm != null && inside(point.from_shore_nm, point.thresholds.from_shore_nm))

  let district: React.ReactNode
  let districtSure: boolean | null = true
  if (several) {
    district = t('rules.point.border', { codes: point.districts.join(', ') })
    districtSure = false
  } else if (d?.status === 'district') {
    district = <>{d.name} <span className="tnum text-mist">{d.code}</span></>
  } else if (d?.status === 'outside') {
    district = t('rules.point.outside')
  } else {
    district = t('rules.point.unknown')
    districtSure = null
  }

  return (
    <section aria-label={t('rules.point.title')} className="border-b border-hull bg-deep/60 px-3 py-2 text-[12px]">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="font-heading text-[13px] font-semibold text-foam">{t('rules.point.title')}</h2>
        <span className="tnum text-[11px] text-faint">
          {point.lat.toFixed(4)}, {point.lon.toFixed(4)}
        </span>
        <button type="button" onClick={onClear} aria-label={t('rules.here.clear')}
          className="ml-auto rounded px-1 text-mist hover:text-foam focus-visible:outline focus-visible:outline-2 focus-visible:outline-sonar">
          ×
        </button>
      </div>
      {point.land && <p className="mt-1 text-buoy">{t('rules.point.land')}</p>}
      {coverageNote(coverage) && <p className="mt-1 text-buoy">{coverageNote(coverage)}</p>}
      <div className="mt-1">
        <Row sure={districtSure} label={t('rules.point.district')}>{district}</Row>
        <Row sure={point.places_edge.length ? false : true} label={t('rules.point.places')}>
          {point.places_here.length + point.places_edge.length === 0
            ? t('rules.point.noPlaces')
            : t('rules.point.placesValue', { here: point.places_here.length, edge: point.places_edge.length })}
        </Row>
        <Row sure={point.depth == null ? null : !inside(point.depth, point.thresholds.depth)}
          label={t('rules.point.depth')}
          scale={point.depth && <Scale interval={point.depth} thresholds={point.thresholds.depth}
            unit={t('rules.unit.m')} fmt={fmt} />}>
          {point.depth ? range(point.depth, t('rules.unit.m'), fmt) : t('rules.point.unknownValue')}
        </Row>
        <Row sure={point.from_shore_nm == null ? null : !inside(point.from_shore_nm, point.thresholds.from_shore_nm)}
          label={t('rules.point.shore')}
          scale={point.from_shore_nm && (
            <Scale interval={point.from_shore_nm} thresholds={point.thresholds.from_shore_nm}
              unit={t('rules.unit.nm')} fmt={fmt} />)}>
          {point.from_shore_nm ? range(point.from_shore_nm, t('rules.unit.nm'), fmt) : t('rules.point.unknownValue')}
        </Row>
      </div>
      <p className="mt-1 text-[11px] text-faint">
        {t('rules.point.radius', { km: fmt(point.radius_m / 1000) })}
        {unsure && ` ${t('rules.point.zoomIn')}`}
      </p>
    </section>
  )
}
